import { useContext, useEffect, useRef, useState } from 'react'
import { getTrackBackground, Range } from 'react-range'
import isServer from '../../../../utils/isServer'
import { t } from '../../../../utils/i18n'
import styles from './_video.module.scss'
import BlurredContent from './BlurredContent'
import { useAuth } from '../../../../utils/auth'
import { SongPageContext } from '..'
import {
  AccousticGuitarOptions,
  AllInstruments,
  BassOptions,
  DrumOptions,
  ElectricGuitarOptions,
  KeyOptions,
  VocalOptions
} from './SongInstruments'
import { useRouter } from 'next/router'
import useInView from 'react-cool-inview'
import PIP from '../../../../components/PIP'
import Image from 'next/legacy/image'
import blurredVideoImg from '@public/images/blurred_video.jpg'
import dynamic from 'next/dynamic'
import classNames from 'classnames'
import { Song, User } from '../../../../__generated__/graphql'
import { wistiaVideoPath } from '../../../../utils/routeHelpers'
import { createRoot } from 'react-dom/client'

const VideoPlayer = dynamic(() => import('../Components/VideoPlayer'), {
  ssr: false
})

export const defaultPlayingVideoInstrument = (
  song: Song,
  currentUser: User
) => {
  const songVideo = song?.video || {}
  const firstVideo = AllInstruments.find(
    (instrument) => songVideo[instrument.value]
  )?.value
  const primaryInstrument = normalizedInstrument(
    songVideo,
    currentUser?.primaryInstrument?.name
  )
  return primaryInstrument || firstVideo || null
}

const normalizedInstrument = (songVideos, instrumentName) => {
  const matches = {
    acoustic_guitar: AccousticGuitarOptions,
    guitar: ElectricGuitarOptions,
    drums: DrumOptions,
    bass: BassOptions,
    keys: KeyOptions,
    vocals: VocalOptions
  }

  const result = matches[instrumentName]?.find(
    (instrument) => songVideos[instrument.value]
  )?.value
  return result
}

const SongVideo: React.FC<{
  song: Song
  setIsPlaying: (value: boolean) => void
  setIsSongHeaderVisible: (value: boolean) => void
  mixerIsPlaying: boolean
}> = ({ song, setIsPlaying, setIsSongHeaderVisible, mixerIsPlaying }) => {
  const videoRef = useRef({
    isVideoPlaying: false,
    isVideoPaused: false
  })

  const playerRef = useRef(null)
  const pipPlayerRef = useRef(null)

  const { observe, inView } = useInView()

  const [isPipReady, setIsPipReady] = useState(!videoRef.current.isVideoPaused)
  const [isPausedVideoVisible, setIsPausedVideoVisible] = useState(false)
  const { playingVideoInstrument } = useContext(SongPageContext)
  const { isAuthenticated, currentUser, isAdmin } = useAuth()
  const router = useRouter()

  const togglePlay = () => {
    videoRef.current.isVideoPaused = false
    videoRef.current.isVideoPlaying = true
  }

  const togglePause = () => {
    videoRef.current.isVideoPaused = true
    videoRef.current.isVideoPlaying = false
  }

  const onReadyHandler = () => {
    setIsSongHeaderVisible(true)
  }

  const onPipReadyHandler = () => {
    hidePipControls()
    setIsPipReady(!videoRef.current.isVideoPaused)
  }

  const onPlayHandler = () => {
    togglePlay()
    setIsPlaying(true)
  }

  const onPipPlayHandler = () => {
    togglePlay()
    playerRef?.current?.getInternalPlayer()?.play()
    pipPlayerRef?.current?.seekTo(playerRef.current.getCurrentTime() + 1)
  }

  const onPauseHandler = () => {
    togglePause()
    setIsPipReady(false)
    setIsPlaying(false)
  }

  const onPipPauseHandler = () => {
    togglePause()
    setIsPausedVideoVisible(true)
    playerRef.current.getInternalPlayer().pause()
    setIsPlaying(false)
  }

  const hidePipControls = () => {
    const pip = document.getElementById('pip')
    const pipControls = Array.from(
      document.getElementsByClassName(
        'w-bottom-bar-lower'
      ) as HTMLCollectionOf<HTMLElement>
    )[1]
    if (pip?.contains(pipControls)) {
      pipControls.style.display = 'none'
    }
  }

  useEffect(() => {
    if (mixerIsPlaying) {
      onPauseHandler()
    }
  }, [mixerIsPlaying])

  useEffect(() => {
    setIsPipReady(false)
    router.events.on('routeChangeStart', onPauseHandler)

    return () => router.events.off('routeChangeStart', onPauseHandler)
  }, [])

  useEffect(() => {
    setIsPausedVideoVisible(false)
  }, [inView])

  // Looping starts
  const applyLoopControl = () => {
    ;(window as any).wistiaInit = (W) => {
      interface LoopState {
        start: number
        end: number
      }
      class LoopVideoControl {
        video: any
        props: any
        dialog: any
        unbinds: any[]
        played = 30
        duration = 20
        LOOP_RANGE_STEP = 1
        LOOP_RANGE_MIN = 0
        LOOP_DURATION = 10
        loopEnabled = false
        loopBoundaries: LoopState = {
          start: this.LOOP_DURATION < 0 ? 0 : this.LOOP_DURATION,
          end: this.played
        }
        loopRoot = null

        static handle = 'loopVideoControl'
        static type = 'control-bar-right'
        static isVideoChrome = true

        loopBoundary = this.loopBoundaryFromState(this.loopBoundaries)

        constructor(video) {
          this.video = video
          this.unbinds = []

          this.duration = Math.round(video.duration())
        }

        mountButton(buttonRoot) {
          buttonRoot.innerHTML = this.buttonText()

          // render
          this.applyLoopRangeContainer()

          // bind
          this.unbinds.push(
            this.video.on('secondchange', (time: number) =>
              this.loopVideo(time)
            )
          )
        }

        buttonText() {
          return `
            <div style="display:flex;align-items:center;justify-content:center;height:100%">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" style="font-weight:bold" viewBox="0 0 16 16">
                <path d="M5.68 5.792 7.345 7.75 5.681 9.708a2.75 2.75 0 1 1 0-3.916ZM8 6.978 6.416 5.113l-.014-.015a3.75 3.75 0 1 0 0 5.304l.014-.015L8 8.522l1.584 1.865.014.015a3.75 3.75 0 1 0 0-5.304l-.014.015L8 6.978Zm.656.772 1.663-1.958a2.75 2.75 0 1 1 0 3.916L8.656 7.75Z"></path>
              </svg>
            </div>
          `
        }

        onClickButton(event) {
          this.switchLoopEnabled(this.loopEnabled)
          this.renderLoopRange()
        }

        loopBoundaryFromState(state: LoopState) {
          return (
            [state.start, state.end] || [this.LOOP_RANGE_MIN, this.duration]
          )
        }

        switchLoopEnabled(enabled) {
          this.loopBoundaries = {
            start: this.played - 30 < 0 ? 0 : this.played - 30,
            end: this.played
          }
          this.loopEnabled = !enabled
        }

        formatSeconds(count) {
          return [
            Math.floor(count / 60 / 60),
            Math.floor((count / 60) % 60),
            Math.floor(count % 60)
          ]
            .join(':')
            .replace(/\b(\d)\b/g, '0$1')
            .replace(/^00\:/, '')
        }

        applyLoopRangeContainer() {
          const playContainer = document.querySelector(
            '[data-handle="playbar"]'
          )
          const loopRangeContainer = document.createElement('div')
          loopRangeContainer.setAttribute('id', 'loopRangeHolder')

          this.loopRoot = createRoot(loopRangeContainer)

          if (playContainer) {
            playContainer.prepend(loopRangeContainer)
          }
        }

        loopVideo(time: number) {
          this.onDuration()
          this.onProgress(time)
          this.renderLoopRange()
        }

        onProgress(time: number) {
          if (
            this.loopEnabled &&
            (Math.floor(time) > this.loopBoundaries.end - 1 ||
              Math.floor(time) < this.loopBoundaries.start)
          ) {
            this.video.time(this.loopBoundaries.start)
          }
        }

        onDuration() {
          this.duration = Math.round(this.video.duration())

          if (this.loopBoundaries.end > this.duration) {
            this.loopBoundaries = { ...this.loopBoundaries, end: this.duration }
          }
          if (this.loopBoundaries.start > this.duration) {
            this.loopBoundaries = {
              ...this.loopBoundaries,
              start: this.duration
            }
          }
        }

        renderLoopRange() {
          this.played =
            this.video.time() < this.played
              ? Math.floor(this.played)
              : Math.floor(this.video.time())
          this.loopBoundary = this.loopBoundaryFromState(this.loopBoundaries)

          if (this.loopRoot)
            this.loopRoot.render(
              <div
                style={{
                  marginLeft: `${this.props.leftControlBarWidth}px`
                }}
                className={`${styles.loopBar} ${
                  this.loopEnabled ? styles.loopActive : ''
                }`}
              >
                <Range
                  values={this.loopBoundary}
                  step={this.LOOP_RANGE_STEP}
                  min={this.LOOP_RANGE_MIN}
                  max={this.duration}
                  onChange={([start, end]) => {
                    this.loopBoundaries = {
                      ...this.loopBoundaries,
                      start,
                      end
                    }
                    this.duration = Math.round(this.video.duration())
                    this.loopBoundary = this.loopBoundaryFromState(
                      this.loopBoundaries
                    )
                    this.renderLoopRange()
                  }}
                  renderTrack={({ props, children }) => (
                    <div
                      onMouseDown={props.onMouseDown}
                      onTouchStart={props.onTouchStart}
                      className={styles.rangeContainer}
                    >
                      <div
                        ref={props.ref}
                        className={styles.rangeStripe}
                        style={{
                          background: getTrackBackground({
                            values: this.loopBoundary,
                            colors: ['transparent', '#e894ebcc', 'transparent'],
                            min: this.LOOP_RANGE_MIN,
                            max: this.duration
                          })
                        }}
                      >
                        {children}
                      </div>
                    </div>
                  )}
                  renderThumb={({ index, props, isDragged }) => (
                    <div
                      {...props}
                      style={{
                        ...props.style
                      }}
                      className={styles.rangeHandler}
                    >
                      {/* {isDragged && ( */}
                      <div className={styles.rangeThumb}>
                        <span>
                          {this.formatSeconds(
                            this.loopBoundary[index].toFixed()
                          )}
                        </span>
                      </div>
                      {/* )} */}

                      <div
                        style={{
                          height: '5px',
                          width: '5px',
                          backgroundColor: isDragged ? '#e728ea' : '#fff'
                        }}
                      />
                    </div>
                  )}
                />
              </div>
            )
        }

        destroy() {
          this.unbinds.map((unbind) => unbind())
          this.unbinds = []
        }
      }

      W.defineControl(LoopVideoControl)
    }
  }
  // Looping ends

  const { songWithVideo } = useContext(SongPageContext)

  const songVideo = songWithVideo?.video || {}

  useEffect(() => {
    if (Object.keys(songVideo).length == 0) setIsSongHeaderVisible(true)
  }, [songVideo])

  const video =
    songVideo[
      playingVideoInstrument ||
        defaultPlayingVideoInstrument(songWithVideo, currentUser)
    ]

  if (!isServer() && video) applyLoopControl()

  const isVideoPaused = videoRef.current.isVideoPaused
  const isVideoPlaying = videoRef.current.isVideoPlaying

  const isDemoUnlogged = !isAuthenticated && video

  const previewButtonParam =
    '?image_play_button_size=1x&image_crop_resized=960x540&image_play_button=1&image_play_button_color=b700bae0'

  const demoThumbnail = songWithVideo?.thumbnail
    ? songWithVideo.thumbnail + previewButtonParam
    : '/images/video_thumbnail_419.jpg'

  const isShown =
    (isAuthenticated && !currentUser?.onFreePlan) || isAdmin || video

  return !isShown ? (
    <BlurredContent
      isSignedIn={isAuthenticated}
      isActive={true}
      isOnFreePlan={currentUser?.onFreePlan}
      message={
        currentUser?.onFreePlan
          ? t('Reactivate my account')
          : isAuthenticated
            ? t('No content available')
            : t('Sign in to unlock this video')
      }
    >
      <div className={styles.imgContainer}>
        <Image
          priority={true}
          quality={20} //no need for good quality for blurred image
          src={blurredVideoImg}
          alt={t('Video not available')}
          width={991}
          height={597}
        />
      </div>
    </BlurredContent>
  ) : (
    <>
      <div
        className={classNames('column', styles.playerContainer)}
        ref={observe}
      >
        <VideoPlayer
          playerRef={playerRef}
          className={styles.video}
          url={wistiaVideoPath(video)}
          playing={isVideoPlaying && !isVideoPaused}
          onReady={onReadyHandler}
          onPlay={onPlayHandler}
          onPause={onPauseHandler}
          onEnded={onPauseHandler}
          controls={true}
          light={isDemoUnlogged && !playingVideoInstrument}
          onClickPreview={onPlayHandler}
        />
        {isDemoUnlogged &&
          !isVideoPlaying &&
          !isVideoPaused &&
          !playingVideoInstrument && (
            <div className={styles.thumbnail}>
              <Image
                priority={true}
                src={demoThumbnail}
                alt={t('Song Video')}
                width={991}
                height={597}
                objectFit="contain"
              />
            </div>
          )}
      </div>
      <PIP
        isVisible={
          !inView && (isVideoPlaying || (isVideoPaused && isPausedVideoVisible))
        }
        title={song.title}
      >
        <VideoPlayer
          playerRef={pipPlayerRef}
          url={wistiaVideoPath(video)}
          playing={isPipReady}
          onReady={onPipReadyHandler}
          onPause={onPipPauseHandler}
          onEnded={onPauseHandler}
          onPlay={onPipPlayHandler}
          width={'100%'}
          height={'100%'}
          volume={0}
          config={{ playerId: 'pip' }}
        />
      </PIP>
    </>
  )
}

export default SongVideo
